import React, { useEffect, useState, useMemo } from "react";
import { 
  Table, 
  Button, 
  Modal, 
  Select, 
  Input, 
  Form, 
  Space, 
  message, 
  Card, 
  Tag,
  Descriptions,
  Row,
  Col,
  Divider
} from "antd";
import { 
  PlusOutlined, 
  EyeOutlined, 
  PhoneOutlined,
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  SearchOutlined
} from "@ant-design/icons";
import api from "../api/Api";
import "./RChamada.css";

const { Option } = Select;
const { TextArea } = Input;
const { Search } = Input;

const GestaoChamadas = () => {
  const [chamadas, setChamadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewVisible, setViewVisible] = useState(false);
  const [selectedChamada, setSelectedChamada] = useState(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState(""); // Estado para o termo de pesquisa

  const [tipo, setTipo] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // 🔹 Listar chamadas
  const fetchChamadas = async () => {
    setLoading(true);
    try {
      const res = await api.ListChamadaRegistada();

      // garante sempre array
      const data = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.chamadas)
            ? res.chamadas
            : [];

      setChamadas(data);
    } catch (err) {
      message.error("Erro ao carregar chamadas");
      setChamadas([]); // segurança extra
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Carrega províncias
  const fetchProvincias = async () => {
    try {
      const res = await api.ListProvincia();
      setProvincias(res);
    } catch {
      message.error("Erro ao carregar províncias");
    }
  };

 

  // 🔹 Carrega planos conforme tipo
  const fetchPlanos = async (tipoSelecionado) => {
    try {
      let res = [];
      if (tipoSelecionado === "Informação") res = await api.ListInformacao();
      if (tipoSelecionado === "Reclamação") res = await api.ListReclamacao();
      if (tipoSelecionado === "Solicitação") res = await api.ListSolicitacao();
      setPlanos(res);
    } catch {
      message.error("Erro ao carregar planos");
    }
  };

  // 🔹 Carrega pacotes conforme plano e tipo
  const fetchPacotes = async (id_plano, tipoSelecionado) => {
    try {
      let res = [];
      const dados = { id: id_plano };
      if (tipoSelecionado === "Informação")
        res = await api.ListPacoteInformacao(dados);
      if (tipoSelecionado === "Reclamação")
        res = await api.ListPacoteReclamacao(dados);
      if (tipoSelecionado === "Solicitação")
        res = await api.ListPacoteSolicitacao(dados);
      setPacotes(res);
    } catch {
      message.error("Erro ao carregar pacotes");
    }
  };

  // 🔹 Ao abrir a tela
  useEffect(() => {
    fetchChamadas();
    fetchProvincias();
  }, []);

  // 🔹 Envio do formulário
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (tipo === "Indisponibilidade") {
        await api.AddIndisponibilidade({
          nome: values.nome,
          telefone: values.telefone,
          ocorrencia: values.ocorrencia,
          obs: values.obs,
          municipio: values.municipio,
          id_provincia: values.id_provincia,
        });
      } else {
        const body = {
          nome: values.nome,
          telefone: values.telefone,
          obs: values.obs,
          municipio: values.municipio,
          id_provincia: values.id_provincia,
        };
        if (tipo === "Informação") body.id_informacoes = values.id_pacote;
        if (tipo === "Reclamação") body.id_reclamacoes = values.id_pacote;
        if (tipo === "Solicitação") body.id_solicitacoes = values.id_pacote;
        await api.AddChamada(body);
      }
      message.success("Chamada adicionada com sucesso!");
      setModalVisible(false);
      form.resetFields();
      setTipo(null);
      setPlanos([]);
      setPacotes([]);
      fetchChamadas();
    } catch {
      message.error("Erro ao adicionar chamada");
    } finally {
      setSubmitting(false);
    }
  };

  // 🔹 Resetar modal
  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setTipo(null);
    setPlanos([]);
    setPacotes([]);
  };

  // 🔹 Função para obter a cor do tag baseado no tipo
  const getTipoColor = (tipo) => {
    switch (tipo) {
      case "Reclamação": return "red";
      case "Informação": return "blue";
      case "Solicitação": return "green";
      case "Indisponibilidade": return "orange";
      default: return "default";
    }
  };

  // 🔹 Filtrar chamadas com base no termo de pesquisa
  const filteredChamadas = useMemo(() => {
    if (!searchTerm) return chamadas;
    
    const term = searchTerm.toLowerCase();
    return chamadas.filter(chamada => 
      chamada.nome?.toLowerCase().includes(term) ||
      chamada.telefone?.includes(term)
    );
  }, [chamadas, searchTerm]);

  // 🔹 Colunas da tabela
  const columns = [
    { 
      title: "Nome", 
      dataIndex: "nome", 
      key: "nome",
      render: (text) => <strong>{text}</strong>
    },
    { 
      title: "Localização", 
      key: "localizacao",
      render: (_, record) => (
        <div>
          <div>{record.municipio}</div>
          <small style={{ color: '#666' }}>{record.provincia}</small>
        </div>
      )
    },
    { 
      title: "Tipo", 
      dataIndex: "tipo_chamada", 
      key: "tipo_chamada",
      render: (tipo) => (
        <Tag color={getTipoColor(tipo)}>
          {tipo?.toUpperCase()}
        </Tag>
      )
    },
    { 
      title: "Plano", 
      dataIndex: "plano", 
      key: "plano",
      render: (plano) => plano || "-"
    },
    { 
      title: "Pacote / Ocorrência", 
      dataIndex: "pacote", 
      key: "pacote",
      render: (pacote) => pacote || "-"
    },
    { 
      title: "Data", 
      dataIndex: "data", 
      key: "data",
      render: (data) => (
        <Space>
          <CalendarOutlined />
          {data}
        </Space>
      )
    },
    {
      title: "Ações",
      key: "acoes",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedChamada(record);
            setViewVisible(true);
          }}
        >
          Detalhes
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <PhoneOutlined />
            Gestão de Chamadas
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            Nova Chamada
          </Button>
        }
      >
        {/* Campo de pesquisa */}
        <div style={{ marginBottom: 16 }}>
          <Search
            
            allowClear
            enterButton={
              <Button type="primary" icon={<SearchOutlined />}>
                Pesquisar
              </Button>
            }
            size="large"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={(value) => setSearchTerm(value)}
            style={{ maxWidth: 400 }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            <small>Mostrando {filteredChamadas.length} de {chamadas.length} chamadas</small>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredChamadas}
          loading={loading}
          rowKey="id"
          scroll={{ x: 800 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} de ${total} chamadas`,
          }}
        />
      </Card>

      {/* Modal de Adição */}
      <Modal
        className="modal-chamadas"
        title={
          <Space>
            <PlusOutlined />
            Nova Chamada
          </Space>
        }
        open={modalVisible}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        okText="Registrar Chamada"
        cancelText="Cancelar"
        width={700}
        confirmLoading={submitting}
      >
        <Divider />
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                label="Tipo de Chamada" 
                name="tipo" 
                rules={[{ required: true, message: 'Selecione o tipo de chamada' }]}
              >
                <Select
                  placeholder="Selecione o tipo"
                  onChange={(value) => {
                    setTipo(value);
                    setPacotes([]);
                    setPlanos([]);
                    form.setFieldsValue({ id_plano: undefined, id_pacote: undefined });
                    if (value !== "Indisponibilidade") fetchPlanos(value);
                  }}
                >
                  <Option value="Reclamação">Reclamação</Option>
                  <Option value="Informação">Informação</Option>
                  <Option value="Solicitação">Solicitação</Option>
                  <Option value="Indisponibilidade">Indisponibilidade</Option>
                </Select>
              </Form.Item>
            </Col>
            
            {tipo && tipo !== "Indisponibilidade" && (
              <>
                <Col span={12}>
                  <Form.Item 
                    label="Plano" 
                    name="id_plano" 
                    rules={[{ required: true, message: 'Selecione o plano' }]}
                  >
                    <Select
                      placeholder="Selecione o plano"
                      onChange={(id) => fetchPacotes(id, tipo)}
                    >
                      {planos.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.plano}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    label="Pacote" 
                    name="id_pacote" 
                    rules={[{ required: true, message: 'Selecione o pacote' }]}
                  >
                    <Select placeholder="Selecione o pacote">
                      {pacotes.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.pacote}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </>
            )}

            {tipo === "Indisponibilidade" && (
              <Col span={24}>
                <Form.Item 
                  label="Ocorrência" 
                  name="ocorrencia" 
                  rules={[{ required: true, message: 'Descreva a ocorrência' }]}
                >
                  <Input placeholder="Descreva a ocorrência" />
                </Form.Item>
              </Col>
            )}

            <Col span={12}>
              <Form.Item 
                label="Província" 
                name="id_provincia" 
                rules={[{ required: true, message: 'Selecione a província' }]}
              >
                <Select
                  placeholder="Selecione a província"
                 
                >
                  {provincias.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.descricao}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item 
                label="Município" 
                name="municipio" 
                rules={[{ required: true, message: 'Digite o município' }]}
              >
                <Input
                  placeholder="Digite o município"
                  prefix={<EnvironmentOutlined />}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item 
                label="Nome" 
                name="nome" 
                rules={[{ required: true, message: 'Digite o nome' }]}
              >
                <Input 
                  placeholder="Digite o nome" 
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item 
                label="Telefone" 
                name="telefone" 
                rules={[{ required: true, message: 'Digite o telefone' }]}
              >
                <Input 
                  placeholder="Digite o telefone" 
                  prefix={<PhoneOutlined />}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Observações" name="obs">
                <TextArea 
                  rows={3} 
                  placeholder="Escreva observações (opcional)" 
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal de Visualização */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            Detalhes da Chamada
          </Space>
        }
        open={viewVisible}
        onCancel={() => setViewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewVisible(false)}>
            Fechar
          </Button>
        ]}
        width={600}
      >
        {selectedChamada && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Nome">
              <Space>
                <UserOutlined />
                {selectedChamada.nome}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Telefone">
              <Space>
                <PhoneOutlined />
                {selectedChamada.telefone}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Localização">
              <Space>
                <EnvironmentOutlined />
                {selectedChamada.municipio} - {selectedChamada.provincia}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Tipo">
              <Tag color={getTipoColor(selectedChamada.tipo_chamada)}>
                {selectedChamada.tipo_chamada}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Plano">
              {selectedChamada.plano || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Pacote / Ocorrência">
              {selectedChamada.pacote || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Data">
              <Space>
                <CalendarOutlined />
                {selectedChamada.data}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Observações">
              {selectedChamada.obs || "Nenhuma observação"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default GestaoChamadas;